/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { useState } from 'react';
import { EmbeddedYoutubeVideo } from '../../../../components/EmbeddedYoutubeVideo';
import {
  LG_SCREEN_MIN,
  MD_SCREEN_MAX,
  MD_SCREEN_MIN,
} from '../../../../modules/Styleguide';
import { CDN_PATH } from '../constants';
import {
  PageHeader,
  ScrollToContainer,
  Section,
  SectionContent,
  SectionHeader,
  ValuePropLabel,
} from '../styled';

export function AnalyticsSection() {
  return (
    <ScrollToContainer id="analytics">
      <StyledSection>
        <StyledSectionContent>
          <ValuePropLabel>Straightforward Analytics</ValuePropLabel>
          <PageHeader>
            Actionable stats to help you track your podcast’s success.
          </PageHeader>
          <div
            css={css`
              padding-top: 75px;
            `}
          >
            <VideoPlayer />
          </div>
        </StyledSectionContent>

        <StyledAnalyticsContent>
          <div>
            <SectionHeader>All the stats, in one place</SectionHeader>
            <p>
              Track metrics from multiple platforms including unique stats
              powered by Spotify, all in a single dashboard. Measure overall
              growth, down-to-the-second episode performance, and manage your
              monetization without ever leaving the app.
            </p>
          </div>
          <div>
            <picture>
              <source
                srcSet={`${CDN_PATH}/audience-chart.svg`}
                media="(min-width: 992px)"
              />
              <img
                // @ts-ignore;
                loading="lazy"
                alt="analytic metrics graph"
                src={`${CDN_PATH}/audience-chart-mobile.svg`}
              />
            </picture>
          </div>
        </StyledAnalyticsContent>
        <StyledAnalyticsContent>
          <div
            css={css`
              @media (min-width: ${MD_SCREEN_MIN}px) and (max-width: ${MD_SCREEN_MAX}px) {
                order: 1;
                img {
                  padding-right: 80px;
                }
              }
            `}
          >
            <SectionHeader>Episode Drop-off</SectionHeader>
            <p>
              See how far listeners are getting into each episode—and where
              they’re dropping off—so that you can plan your podcast around the
              content that performs best. Compare the average listening time
              across multiple episodes and learn which segments are keeping
              listeners engaged.
            </p>
          </div>
          <div
            css={css`
              @media (min-width: ${MD_SCREEN_MIN}px) and (max-width: ${MD_SCREEN_MAX}px) {
                img {
                  padding-left: 0;
                  padding-right: 80px;
                }
              }
            `}
          >
            <picture>
              <source
                type="image/webp"
                srcSet={`${CDN_PATH}/ep-dropoff-chart.webp`}
                media="(min-width: 992px)"
              />
              <source
                srcSet={`${CDN_PATH}/ep-dropoff-chart.jpeg`}
                media="(min-width: 992px)"
              />
              <source
                type="image/webp"
                srcSet={`${CDN_PATH}/ep-dropoff-chart-mobile.webp`}
              />
              <img
                // @ts-ignore;
                loading="lazy"
                alt="episode drop-off chart"
                src={`${CDN_PATH}/ep-dropoff-chart-mobile.jpeg`}
              />
            </picture>
          </div>
        </StyledAnalyticsContent>
        <StyledAnalyticsContent>
          <div>
            <SectionHeader>Listener Demographics</SectionHeader>
            <p>
              Learn more about your listeners with insights that break down the
              aggregated age, gender, geolocation, and listening platform of
              your audience.
            </p>
          </div>
          <div>
            <picture>
              <source
                type="image/webp"
                srcSet={`${CDN_PATH}/listener-demographic-chart.webp`}
              />
              <img
                // @ts-ignore;
                loading="lazy"
                alt="listener demographic chart"
                src={`${CDN_PATH}/listener-demographic-chart.png`}
              />
            </picture>
          </div>
        </StyledAnalyticsContent>
      </StyledSection>
    </ScrollToContainer>
  );
}

function VideoPlayer() {
  const [showVideo, toggleVideo] = useState(false);

  return showVideo ? (
    <EmbeddedYoutubeVideo
      title="Anchor | Introducing advanced podcast analytics"
      youtubeVideoId="kSbvW_pCgmU"
      paddingBottomOffset="56.25%"
      youtubeEmbedParameters={{
        autoplay: 1,
        controls: 1,
        modestbranding: 1,
        loop: 1,
      }}
    />
  ) : (
    <button type="button" onClick={() => toggleVideo(true)}>
      <picture>
        <source
          type="image/webp"
          srcSet={`${CDN_PATH}/analytics-videothumb.webp`}
        />
        <img
          // @ts-ignore;
          loading="lazy"
          width="100%"
          alt="analytics video thumbnail"
          src={`${CDN_PATH}/analytics-videothumb.jpeg`}
        />
      </picture>
    </button>
  );
}

const StyledSection = styled(Section)`
  background: white;
  h2,
  h4 {
    color: #292f36;
  }
  padding-top: 75px;
  padding-bottom: 75px;
`;

const StyledSectionContent = styled(SectionContent)`
  flex-direction: column;
  padding-top: 50px;
  padding-bottom: 50px;
`;

const StyledAnalyticsContent = styled(StyledSectionContent)`
  padding-top: 75px;
  padding-bottom: 0;
  img {
    width: 100%;
    margin-top: 30px;
  }
  @media (min-width: ${MD_SCREEN_MIN}px) {
    padding-bottom: 75px;
    flex-direction: row;
    img {
      padding-left: 80px;
      margin-top: 0px;
    }
    & > div:first-child {
      width: 30%;
    }
    & > div:last-child {
      width: 70%;
    }
  }
  @media (min-width: ${LG_SCREEN_MIN}) {
    padding-top: 100px;
    padding-bottom: 100px;
  }
`;
